<!--
This file is part of the project: "Digiziege"
An app for animal-welfare-oriented goat husbandry and contactless weight estimation.
GitLab: https://gitlab.rlp.net/proj-wise2526-digiziege/digiziege

Copyright (c) 2026 Digiziege Team TH-Bingen

Description:
Companion app for welfare-oriented goat husbandry featuring contactless weight estimation via camera,
master data management and health tracking.
Built as a PWA with a local-first approach to ensure data privacy.

LICENSE INFORMATION:
This program is free software: you can redistribute it and/or modify it under the terms of the
GNU Affero General Public License as published by the Free Software Foundation,
either version 3 of the License, or (at your option) any later version.

This program is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
See the GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License along with this program.
If not, see <https://www.gnu.org/licenses/>.
-->

<template>
  <div class="flex flex-col h-full">
    <div class="mb-4">
      <div class="flex gap-2">
        <!--Searchbar-->
        <TableRefreshActionModule
          :loading="loading"
          @trigger-refresh="handleRefresh" />
        <div v-if="enableSearch" class="relative flex-1">
          <input
            v-model="searchQuery"
            type="text"
            :placeholder="$t('common.tableList.searchbar.placeholder')"
            class="input-field !pl-10 h-[42px]" />
          <div
            class="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--color-primary-500)]">
            <Search class="w-5 h-5" />
          </div>
          <button
            v-if="searchQuery"
            @click="searchQuery = ''"
            class="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--color-primary-500)] p-1">
            <X class="w-4 h-4" />
          </button>
        </div>
        <!--Toggle Switch: Change Mode from Simple View to Extend for more info-->
        <div
          @click="isSimpleView = !isSimpleView"
          class="flex items-center bg-[var(--color-primary-200)] rounded-lg p-1 cursor-pointer border border-[var(--color-primary-400)] shrink-0 select-none h-[42px]"
          :title="
            isSimpleView
              ? 'Zu Detailansicht wechseln'
              : 'Zu Kompaktansicht wechseln'
          ">
          <div
            class="w-10 h-full flex items-center justify-center rounded-md transition-all duration-200"
            :class="
              isSimpleView
                ? 'bg-[var(--color-bg)] shadow text-[var(--color-primary-900)]'
                : 'text-[var(--color-primary-400)] hover:text-[var(--color-primary-600)]'
            ">
            <List class="w-5 h-5" stroke-width="2.5" />
          </div>
          <div
            class="w-10 h-full flex items-center justify-center rounded-md transition-all duration-200"
            :class="
              !isSimpleView
                ? 'bg-[var(--color-bg)] shadow text-[var(--color-primary-900)]'
                : 'text-[var(--color-primary-400)] hover:text-[var(--color-primary-600)]'
            ">
            <Table2 class="w-5 h-5" stroke-width="2.5" />
          </div>
        </div>
      </div>
    </div>
    <!--Table-->
    <div
      class="flex-1 overflow-hidden relative rounded-lg border border-[var(--color-primary-400)] shadow-lg bg-[var(--color-primary-100)] flex flex-col">
      <!--Loading Overlay for Table-->
      <div
        v-if="loading"
        class="absolute inset-0 z-50 bg-[var(--color-primary-100)] bg-opacity-80 flex items-center justify-center">
        <div
          class="animate-spin w-8 h-8 border-4 border-[var(--color-primary-500)] border-t-transparent rounded-full"></div>
      </div>

      <div class="overflow-x-auto flex-1">
        <table class="min-w-full border-collapse">
          <thead
            class="bg-[var(--color-primary-200)] border-b-2 border-[var(--color-primary-400)] sticky top-0 z-20 shadow-sm">
            <tr>
              <th
                v-for="(col, index) in visibleColumns"
                :key="col.key"
                @click="sortBy(col.key)"
                class="px-4 py-3 text-left text-xs font-bold text-[var(--color-primary-900)] uppercase tracking-wider whitespace-nowrap select-none cursor-pointer hover:bg-[var(--color-primary-300)] transition-colors"
                :class="{
                  'sticky left-0 z-30 bg-[var(--color-primary-200)] border-r border-[var(--color-primary-300)]':
                    index === 0,
                }">
                <!--Sort-->
                <div class="flex items-center gap-1">
                  {{ $t(col.label) }}
                  <span
                    v-if="sortKey === col.key"
                    class="text-[var(--color-primary-600)]">
                    <ArrowUp v-if="sortOrder === 'asc'" class="w-4 h-4" />
                    <ArrowDown v-else class="w-4 h-4" />
                  </span>
                </div>
              </th>
            </tr>
          </thead>

          <tbody class="divide-y divide-[var(--color-primary-300)]">
  <tr
    v-for="item in processedData"
    :key="item.id"
    @click="$emit('row-click', item)"
    class="group cursor-pointer transition-colors duration-150">
    <td
      v-for="(col, index) in visibleColumns"
      :key="col.key"
      class="px-4 py-4 whitespace-nowrap text-sm text-[var(--color-text)] font-medium
             group-hover:bg-[var(--color-primary-200)]
             group-active:bg-[var(--color-primary-300)]"
      :class="{
        'sticky left-0 z-10 border-r border-[var(--color-primary-300)] bg-[var(--color-primary-100)] group-hover:bg-[var(--color-primary-200)] group-active:bg-[var(--color-primary-300)]':
          index === 0,
      }">
      <slot
        :name="`cell-${col.key}`"
        :item="item"
        :value="item[col.key]">
        {{ formatValue(item[col.key], col.type) }}
      </slot>
    </td>
  </tr>

  <!-- If no entries found -->
  <tr v-if="processedData.length === 0 && !loading">
    <td
      :colspan="visibleColumns.length"
      class="px-6 py-12 text-center text-[var(--color-primary-500)]">
      <div class="flex flex-col items-center gap-2">
        <SearchX class="w-8 h-8 text-[var(--color-primary-400)]" />
        <span
          class="text-base text-[var(--color-primary-600)] font-semibold">
          {{ $t('common.tableList.noEntries') }}
        </span>
      </div>
    </td>
  </tr>
</tbody>

        </table>
      </div>
      <!--Table Footer-->
      <div
        class="bg-[var(--color-primary-200)] px-4 py-2 text-xs text-[var(--color-primary-600)] border-t border-[var(--color-primary-400)] flex justify-between items-center">
        <span
          >{{ processedData.length }} {{ $t('common.tableList.Entries') }}</span
        >
        <span v-if="processedData.length !== data.length"
          >{{ $t('common.tableList.filteredEntries') }} {{ data.length }}</span
        >
      </div>
    </div>
  </div>
</template>

<script setup>
  import { ref, computed } from 'vue'
  import { useI18n } from 'vue-i18n'
  import TableRefreshActionModule from '@/components/actions/TableRefreshAction.vue'
  import {
    List,
    Table2,
    Search,
    X,
    ArrowUp,
    ArrowDown,
    SearchX,
    RefreshCcw,
  } from 'lucide-vue-next'

  const props = defineProps({
    data: { type: Array, required: true, default: () => [] },
    columns: { type: Array, required: true },
    enableSearch: { type: Boolean, default: true },
    loading: { type: Boolean, default: false },
  })

  const emit = defineEmits(['row-click', 'refresh'])

  const handleRefresh = () => {
    emit('refresh')
  }

  // States
  const searchQuery = ref('')
  const sortKey = ref(null)
  const sortOrder = ref('asc')
  const isSimpleView = ref(true)

  const { locale } = useI18n()

  const formatDateInternal = (val) => {
    if (!val) return ''
    return new Date(val).toLocaleDateString(locale.value, {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    })
  }

  // visible columns const for toggle switch filtering important columns
  const visibleColumns = computed(() => {
    if (isSimpleView.value) return props.columns.filter((col) => col.simple)
    return props.columns
  })

  // sort const for column asc and desc
  const sortBy = (key) => {
    if (sortKey.value === key) {
      sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc'
    } else {
      sortKey.value = key
      sortOrder.value = 'asc'
    }
  }

  const formatValue = (val, type) => {
    if (val === null || val === undefined) return '-'
    if (type === 'date') return formatDateInternal(val)
    return val
  }

  // searching process
  const processedData = computed(() => {
    let result = [...props.data]

    if (searchQuery.value) {
      const rawQuery = searchQuery.value.trim()
      const lowerQuery = rawQuery.toLowerCase()

      //gender search has special case because male is part of female spelling
      if (lowerQuery === 'male' || lowerQuery === 'bock') {
        result = result.filter((item) => item.gender === 'Male')
      } else if (lowerQuery === 'female' || lowerQuery === 'ziege') {
        result = result.filter((item) => item.gender === 'Female')
      }
      // 2. Standard Suche (Fuzzy)
      else {
        result = result.filter((item) => {
          // all columns are beeing searched
          return props.columns.some((col) => {
            const val = item[col.key]
            if (val === null || val === undefined) return false

            // date logics
            if (col.type === 'date') {
              const dateStr = formatDateInternal(val).toLowerCase()
              // years
              return (
                dateStr.includes(lowerQuery) || String(val).includes(lowerQuery)
              )
            }

            // default string search
            return String(val).toLowerCase().includes(lowerQuery)
          })
        })
      }
    }

    // sorting logic, only sort if a key is selected
    if (sortKey.value) {
      result.sort((a, b) => {
        let valA = a[sortKey.value]
        let valB = b[sortKey.value]
        //null values to end of list
        if (valA === null) return 1
        if (valB === null) return -1
        //Data Type for comarison. Check if both values are valid
        if (
          !isNaN(parseFloat(valA)) &&
          isFinite(valA) &&
          !isNaN(parseFloat(valB)) &&
          isFinite(valB)
        ) {
          //correct number sorting
          valA = parseFloat(valA)
          valB = parseFloat(valB)
        } else {
          //correct string sorting convert to lowercase
          valA = String(valA).toLowerCase()
          valB = String(valB).toLowerCase()
        }
        //comparison based on sort order
        if (valA < valB) return sortOrder.value === 'asc' ? -1 : 1
        if (valA > valB) return sortOrder.value === 'asc' ? 1 : -1
        return 0
      })
    }

    return result
  })
</script>
